import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';

export type AccountRelationErrorReason =
  'limitReached' | 'notActive' | 'nameTaken' | 'rateLimit' | 'notAMasterAccountAccountant';

/** The `createModal.error.*` / `disconnectModal.error.*` key suffix of a client refusal, if known. */
export function getAccountRelationErrorReason(
  error: unknown
): AccountRelationErrorReason | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  for (const gqlError of error.errors) {
    const code = gqlError.extensions?.['code'] as string | undefined;
    switch (code) {
      case GraphQLErrorCode.ACCOUNT_RELATION_LIMIT_REACHED:
        return 'limitReached';
      case GraphQLErrorCode.MASTER_ACCOUNT_ACCOUNT_NOT_ACTIVE:
      case GraphQLErrorCode.MASTER_ACCOUNT_PENDING_DELETION:
        return 'notActive';
      case GraphQLErrorCode.ACCOUNT_NAME_TAKEN:
        return 'nameTaken';
      case GraphQLErrorCode.USER_NOT_A_MASTER_ACCOUNT_ACCOUNTANT:
        return 'notAMasterAccountAccountant';
      case GraphQLErrorCode.RATE_LIMIT_EXCEEDED:
        return 'rateLimit';
    }
  }

  return undefined;
}

/**
 * Whether a client could not leave its master account because an organization outside any master account already
 * uses its name — the client has to be renamed first (`disconnectManagedClient`, `leaveManagingMasterAccount`).
 */
export function isDetachNameTakenError(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      gqlError => gqlError.extensions?.['code'] === GraphQLErrorCode.DETACH_ACCOUNT_NAME_TAKEN
    )
  );
}

/** Whether a revoke found the pair no longer assigned (a double click, a concurrent revoke). */
export function isAssignmentNotFoundError(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(gqlError => gqlError.extensions?.['code'] === GraphQLErrorCode.NOT_FOUND)
  );
}

/** The `disconnectModal.error.*` / `deleteModal.error.*` key suffix of a refused detach. */
export type AccountRelationDetachErrorReason =
  'detachNameTaken' | 'noOwnUsers' | 'hasOwnUsers' | 'needsSupport';

const DETACH_REASON_BY_CODE: Readonly<Record<string, AccountRelationDetachErrorReason>> = {
  [GraphQLErrorCode.DETACH_ACCOUNT_NAME_TAKEN]: 'detachNameTaken',
  [GraphQLErrorCode.ACCOUNT_RELATION_HAS_NO_OWN_USERS]: 'noOwnUsers',
  [GraphQLErrorCode.ACCOUNT_RELATION_HAS_OWN_USERS]: 'hasOwnUsers',
  [GraphQLErrorCode.ACCOUNT_RELATION_DELETION_NEEDS_SUPPORT]: 'needsSupport'
};

/** Why disconnecting or deleting a client was refused, if it is one of the known reasons. */
export function getAccountRelationDetachErrorReason(
  error: unknown
): AccountRelationDetachErrorReason | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  for (const gqlError of error.errors) {
    const code = gqlError.extensions?.['code'];
    if (typeof code === 'string' && Object.hasOwn(DETACH_REASON_BY_CODE, code)) {
      return DETACH_REASON_BY_CODE[code];
    }
  }

  return undefined;
}
